/**
 * Assets as plain data: names, folders, sizes, what a listing holds in a folder. The service
 * that talks to the core is `state/assets.ts`.
 */
import type { AssetInfo } from "@protocol/AssetInfo";
import type { AssetListing } from "@protocol/AssetListing";
import type { AssetSource } from "@protocol/AssetSource";

/** The biggest asset the core keeps (the same as `MAX_ASSET_BYTES` in `da-types`). */
export const MAX_ASSET_BYTES = 10 * 1024 * 1024;

/** The last part of a path (`art/cover.png` → `cover.png`). */
export function assetName(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** A name without its extension (`cover.png` → `cover`), to name a node after a file. */
export function assetStem(path: string): string {
  const name = assetName(path);
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

/** The folder a path is in (`""`: the top). */
export function parentOf(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash < 0 ? "" : path.slice(0, slash);
}

/** `name` inside `folder`. */
export function joinPath(folder: string, name: string): string {
  return folder ? `${folder}/${name}` : name;
}

/** The folders right inside `folder`. */
export function childFolders(listing: AssetListing, folder: string): string[] {
  return listing.folders.filter((f) => parentOf(f) === folder && f !== folder);
}

/** The assets right inside `folder`, or with `query` every asset below it whose path holds it (any case). */
export function filesIn(listing: AssetListing, folder: string, query = ""): AssetInfo[] {
  const q = query.trim().toLowerCase();
  if (!q) return listing.files.filter((f) => parentOf(f.path) === folder);
  const below = folder ? `${folder}/` : "";
  return listing.files.filter((f) => f.path.startsWith(below) && f.path.slice(below.length).toLowerCase().includes(q));
}

/** How many assets are in `folder` and below. */
export function countBelow(listing: AssetListing, folder: string): number {
  return listing.files.filter((f) => f.path.startsWith(`${folder}/`)).length;
}

/** A size for people: `512 B`, `3.4 kB`, `2.1 MB`. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The extension shown on a card (`PNG`), or `FILE`. */
export function extensionOf(path: string): string {
  const name = assetName(path);
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toUpperCase().slice(0, 5) : "FILE";
}

/** The extensions of the pictures a page or a box can show. */
const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg", "avif", "bmp", "ico"]);

/** Whether `path` names a picture (from its extension, like the core's `AssetKind::of`). */
export function isImagePath(path: string): boolean {
  const name = assetName(path);
  const dot = name.lastIndexOf(".");
  return dot > 0 && IMAGE_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
}

/** The picture a node shows (in its nodal shape, in its diagram box): its first attached image. */
export function pictureOf(attachments: readonly string[] | undefined): string | null {
  return attachments?.find(isImagePath) ?? null;
}

/** What a page writes to show an asset: `![[art/cover.png]]`. */
export function embedOf(path: string): string {
  return `![[${path}]]`;
}

/** The path an asset has after `from` moved to `to` (a file, or a folder it was in), or itself. */
export function movedPath(path: string, from: string, to: string): string {
  if (path === from) return to;
  return path.startsWith(`${from}/`) ? to + path.slice(from.length) : path;
}

/**
 * The event a file dragged from the drawer is told with when it is dropped on an element
 * marked `data-asset-target` (the board, a document): `detail` is an {@link AssetDrop}.
 */
export const ASSET_DROP = "knodal-asset-drop";

/** A file dropped from the drawer: where it is kept and the page point it was dropped at. */
export interface AssetDrop {
  source: AssetSource;
  path: string;
  x: number;
  y: number;
}
